import React from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  Image,
  Platform,
} from 'react-native';

import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { COLORS, FONTS, SSO_FRONTEND_URL } from '../../constants/config';
import { AccessDenialCode } from '../../constants/accessDenial';
import { shadows } from '../../utils/styles';

import { tokens } from '@design';
type ActionKind = 'citizen_portal' | 'apps_portal' | 'retry_login';

interface DenialAction {
  kind: ActionKind;
  labelKey: string;
  primary: boolean;
}

const ACTIONS_BY_CODE: Record<AccessDenialCode, DenialAction[]> = {
  citizen_blocked: [
    { kind: 'citizen_portal', labelKey: 'accessDenied.goCitizenPortal', primary: true },
  ],
  position_type_missing: [
    { kind: 'retry_login', labelKey: 'accessDenied.retryLogin', primary: true },
    { kind: 'apps_portal', labelKey: 'accessDenied.backToApps', primary: false },
  ],
  no_role: [
    { kind: 'retry_login', labelKey: 'accessDenied.retryLogin', primary: true },
    { kind: 'apps_portal', labelKey: 'accessDenied.backToApps', primary: false },
  ],
  account_inactive: [
    { kind: 'retry_login', labelKey: 'accessDenied.retryLogin', primary: true },
  ],
  not_authenticated: [
    { kind: 'retry_login', labelKey: 'accessDenied.retryLogin', primary: true },
  ],
  no_system_access: [
    { kind: 'retry_login', labelKey: 'accessDenied.retryLogin', primary: true },
  ],
};

function redirectTo(url: string): void {
  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    window.location.href = url;
  }
}

export default function AccessDeniedScreen() {
  const { accessDenial, clearAccessDenial, logout } = useAuth();
  const { t, language } = useLanguage();

  if (!accessDenial) {
    // Defensive guard — shouldn't render without a denial, but avoid a crash.
    return null;
  }

  const isArabic = language === 'ar';
  const code = accessDenial.code;

  // Prefer the backend-supplied bilingual message; fall back to the locale file
  // (handy if the backend is on an older version that only sends `detail`).
  const backendMessage = isArabic ? accessDenial.detail_ar : accessDenial.detail_en;
  const body = backendMessage || accessDenial.detail || t(`accessDenied.${code}`);

  const actions = ACTIONS_BY_CODE[code] || ACTIONS_BY_CODE.no_system_access;

  const handleAction = async (kind: ActionKind) => {
    if (kind === 'citizen_portal') {
      redirectTo(`${SSO_FRONTEND_URL}/citizen`);
      return;
    }
    if (kind === 'apps_portal') {
      redirectTo(`${SSO_FRONTEND_URL}/apps`);
      return;
    }
    // retry_login: clear denial + kick off the normal logout/redirect flow
    clearAccessDenial();
    await logout();
  };

  return (
    <View style={styles.container}>
      <Image
        source={require('../../../assets/images/Pattern BG.svg')}
        style={styles.patternBackground}
        resizeMode="cover"
      />
      <ScrollView contentContainerStyle={styles.scrollContent}>
        <View style={styles.card}>
          <Image
            source={require('../../../assets/logo/logo-white.png')}
            style={styles.logo}
            resizeMode="contain"
          />
          <Text style={styles.title}>{t('accessDenied.title')}</Text>
          <Text style={[styles.body, isArabic && styles.bodyRtl]}>{body}</Text>
          <View style={styles.actions}>
            {actions.map((action) => (
              <TouchableOpacity
                key={action.kind}
                style={[
                  styles.button,
                  action.primary ? styles.buttonPrimary : styles.buttonSecondary,
                ]}
                onPress={() => handleAction(action.kind)}
              >
                <Text
                  style={[
                    styles.buttonText,
                    action.primary
                      ? styles.buttonTextPrimary
                      : styles.buttonTextSecondary,
                  ]}
                >
                  {t(action.labelKey)}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: COLORS.background,
  },
  patternBackground: {
    ...StyleSheet.absoluteFillObject,
    opacity: 0.03,
  },
  scrollContent: {
    flexGrow: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: tokens.spacing.absolute["6"],
    paddingVertical: tokens.spacing.absolute["12"],
  },
  card: {
    width: '100%',
    maxWidth: 480,
    backgroundColor: COLORS.card,
    borderRadius: tokens.radii.absolute["radius-16"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: COLORS.cardBorder,
    padding: tokens.spacing.absolute["8"],
    alignItems: 'center',
    gap: tokens.spacing.absolute["4"],
    ...shadows.lg,
  },
  logo: {
    width: 96,
    height: 96,
    tintColor: COLORS.primary,
    marginBottom: tokens.spacing.absolute["2"],
  },
  title: {
    fontSize: 22,
    fontFamily: FONTS.medium,
    color: COLORS.neutral900,
    textAlign: 'center',
  },
  body: {
    fontSize: 15,
    lineHeight: 22,
    fontFamily: FONTS.regular,
    color: COLORS.textMuted,
    textAlign: 'center',
  },
  bodyRtl: {
    writingDirection: 'rtl',
  },
  actions: {
    width: '100%',
    marginTop: tokens.spacing.absolute["4"],
    gap: tokens.spacing.absolute["3"],
  },
  button: {
    width: '100%',
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingVertical: tokens.spacing.absolute["3"],
    paddingHorizontal: tokens.spacing.absolute["4"],
    alignItems: 'center',
    justifyContent: 'center',
  },
  buttonPrimary: {
    backgroundColor: COLORS.primary,
  },
  buttonSecondary: {
    backgroundColor: COLORS.white,
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: COLORS.border,
  },
  buttonText: {
    fontSize: 15,
    fontFamily: FONTS.medium,
  },
  buttonTextPrimary: {
    color: COLORS.white,
  },
  buttonTextSecondary: {
    color: COLORS.neutral800,
  },
});
